import { forwardRef, ReactElement, Ref } from 'react';

import {
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  Slide,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

import { DialogTitle } from '../components';

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement<any, any> },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface CustomButton {
  label: string;
  props: ButtonProps;
}

interface AppDialogProps extends Omit<DialogProps, 'title'> {
  title?: string | JSX.Element;
  isLocked?: boolean;
  confirmButton?: CustomButton;
  closeButton?: CustomButton;
}

const AppDialog = ({
  open,
  title = '',
  onClose,
  isLocked,
  fullWidth = true,
  children,
  maxWidth = 'md',
  confirmButton,
  closeButton,
  ...dialogProps
}: AppDialogProps) => (
  <Dialog
    TransitionComponent={Transition}
    open={open}
    fullWidth={fullWidth}
    maxWidth={maxWidth}
    onClose={(event, reason) => {
      if ((isLocked && reason !== 'backdropClick') || !isLocked) {
        onClose(event, reason);
      }
    }}
    PaperProps={{ sx: { root: { minHeight: '90px' } } }}
    {...dialogProps}
  >
    <DialogTitle onClose={() => onClose(null, 'escapeKeyDown')}>{title}</DialogTitle>
    {children && <DialogContent>{children}</DialogContent>}
    {(closeButton || confirmButton) && (
      <DialogActions sx={{ p: 2 }}>
        {confirmButton && (
          <Button
            data-testid="confirm-button"
            variant="contained"
            autoFocus
            {...confirmButton.props}
          >
            {confirmButton.label}
          </Button>
        )}
        {closeButton && (
          <Button data-testid="close-button" variant="outlined" autoFocus {...closeButton.props}>
            {closeButton.label}
          </Button>
        )}
      </DialogActions>
    )}
  </Dialog>
);

export default AppDialog;
